import { Dialog, MobileDialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Check } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

interface CompanySubscriptionTier {
  id: string;
  tier_key: string;
  tier_name: string;
  monthly_price: number;
  job_posting_limit: number | null;
  monthly_credits: number;
  credit_price: number;
  // Deprecated fields (kept for backwards compatibility)
  free_resume_reviews: number;
  free_ai_interviews: number;
  resume_review_price: number;
  ai_interview_price: number;
  is_active: boolean;
}

interface PlansDialogProps {
  open: boolean;
  onClose: () => void;
  subscriptionTiers: CompanySubscriptionTier[];
  currentTier?: CompanySubscriptionTier | null;
  onPlanSelected: (tier: CompanySubscriptionTier) => void;
  isTemporaryPlan?: boolean;
  hasStripeSubscription?: boolean;
}


export function PlansDialog({ 
  open, 
  onClose, 
  subscriptionTiers, 
  currentTier, 
  onPlanSelected,
  isTemporaryPlan = false,
  hasStripeSubscription = false
}: PlansDialogProps) {
  const { t } = useTranslation('pages');

  const formatPrice = (priceInCents: number) => {
    return `$${(priceInCents / 100).toFixed(2)}`;
  };

  const handleTierSelect = (tier: CompanySubscriptionTier) => {
    onPlanSelected(tier);
    onClose();
  };

  // Tier descriptions
  const getTierDescription = (tierKey: string): string => {
    const key = `billing.tier_description_${tierKey}` as const;
    return t(key);
  };

  // Feature lists for each tier
  const getTierFeatures = (tierKey: string): string[] => {
    const featureKeys: Record<string, string[]> = {
      free: ['1', '2', '3', '4', '5'],
      premium: ['1', '2', '3', '4', '5', '6', '7'],
      premium_plus: ['1', '2', '3', '4', '5', '6', '7', '8']
    };
    
    const keys = featureKeys[tierKey] || [];
    return keys.map(num => t(`billing.feature_${tierKey}_${num}` as any));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <MobileDialogContent className="md:max-w-6xl md:overflow-y-auto overscroll-contain">
        <DialogHeader className="space-y-2 pb-4 md:pb-6">
          <DialogTitle className="text-xl sm:text-2xl">{t('billing.change_plan_title')}</DialogTitle>
          <DialogDescription className="text-sm">
            {t('billing.change_plan_description')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-shrink min-h-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 pb-2 px-1 py-1">
          {subscriptionTiers.map((tier) => {
            // Only consider it "current plan" if it's the same tier AND not a temporary plan
            const isCurrentPlan = currentTier?.tier_key === tier.tier_key && !isTemporaryPlan && hasStripeSubscription;
            const isUpgradeFromGift = currentTier?.tier_key === tier.tier_key && isTemporaryPlan;
            const features = getTierFeatures(tier.tier_key);
            const description = getTierDescription(tier.tier_key);
            
            return (
              <Card 
                key={tier.id}
              className={cn(
                "relative flex flex-col transition-all duration-300 ease-out",
                isCurrentPlan
                  ? "bg-gradient-to-br from-primary/5 via-background to-primary/3 ring-2 ring-primary/20 shadow-lg shadow-primary/10 border-transparent"
                  : "bg-card border border-border hover:border-primary/30 active:border-primary/40 hover:shadow-md active:shadow-lg hover:-translate-y-0.5 active:translate-y-0.5 touch-manipulation"
              )}
              >
                {isCurrentPlan && (
                  <Badge 
                    variant="default" 
                    className="absolute top-4 right-4 z-10 shadow-md"
                  >
                    {t('billing.current_plan')}
                  </Badge>
                )}
                
                <CardContent className="p-4 sm:p-6 flex flex-col flex-1">
                  {/* Header Section */}
                  <div className="text-center space-y-2 pb-4 sm:pb-6 border-b">
                    <h3 className="font-bold text-lg sm:text-xl">{tier.tier_name}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground min-h-[2.5rem]">
                      {description}
                    </p>
                  </div>

                  {/* Pricing Section */}
                  <div className="text-center py-4 sm:py-6 space-y-1">
                    <div>
                      {tier.monthly_price === 0 ? (
                        isCurrentPlan ? (
                          <div className="inline-block px-4 py-2 rounded-full bg-primary/5">
                            <span className="text-3xl sm:text-4xl font-bold">{t('billing.free')}</span>
                          </div>
                        ) : (
                          <span className="text-3xl sm:text-4xl font-bold">{t('billing.free')}</span>
                        )
                      ) : (
                        isCurrentPlan ? (
                          <div className="inline-block px-4 py-2 rounded-full bg-primary/5">
                            <span className="text-3xl sm:text-4xl font-bold">
                              {formatPrice(tier.monthly_price)}
                            </span>
                            <span className="text-muted-foreground ml-2 text-sm">
                              {t('billing.per_month')}
                            </span>
                          </div>
                        ) : (
                          <>
                            <span className="text-3xl sm:text-4xl font-bold">
                              {formatPrice(tier.monthly_price)}
                            </span>
                            <span className="text-muted-foreground ml-2 text-sm">
                              {t('billing.per_month')}
                            </span>
                          </>
                        )
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('billing.shared_across_team')}
                    </p>
                  </div>

                  {/* CTA Button */}
              <Button 
                variant={isCurrentPlan ? "secondary" : "default"}
                className="w-full mb-4 sm:mb-6 min-h-[44px] touch-manipulation active:scale-95 transition-transform"
                disabled={isCurrentPlan}
                onClick={() => handleTierSelect(tier)}
              >
                {isCurrentPlan 
                  ? t('billing.current_plan_button') 
                  : isUpgradeFromGift
                    ? t('billing.upgrade_to_paid')
                    : t('billing.select_plan')
                }
              </Button>

                  {/* Features List */}
                  <div className="space-y-2 sm:space-y-3 flex-1">
                    {features.map((feature, index) => {
                      const isInheritedFeature = index === 0 && (
                        feature.toLowerCase().includes('everything in') ||
                        feature.toLowerCase().includes('todo lo de') ||
                        feature.toLowerCase().includes('tout ce qui est') ||
                        feature.toLowerCase().includes('の全機能') ||
                        feature.toLowerCase().includes('的所有功能')
                      );
                      
                      return (
                        <div key={index} className="flex items-start gap-2 sm:gap-3">
                          {isCurrentPlan ? (
                            <div className="rounded-full bg-primary/10 p-1.5 flex-shrink-0">
                              <Check className="h-3.5 w-3.5 text-primary" />
                            </div>
                          ) : (
                            <Check className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0 mt-0.5" />
                          )}
                          <span className={cn(
                            "text-xs sm:text-sm leading-relaxed",
                            isInheritedFeature 
                              ? "text-foreground font-semibold" 
                              : "text-foreground"
                          )}>
                            {feature}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Additional Credits Info */}
                  <div className="pt-3 sm:pt-4 mt-3 sm:mt-4 border-t">
                    <p className="text-xs text-muted-foreground text-center">
                      {t('billing.additional_credits_price', { price: formatPrice(tier.credit_price) })}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          </div>
        </div>
      </MobileDialogContent>
    </Dialog>
  );
}